/**
 * Permission Context Provider
 *
 * Provides user permissions throughout the React app.
 * Loads permissions once on mount and caches them.
 *
 * Usage:
 * 1. Wrap your app with <PermissionProvider>
 * 2. Use usePermissions() hook in any component
 *
 * @example
 * // In App.jsx
 * <PermissionProvider>
 *   <YourApp />
 * </PermissionProvider>
 *
 * // In any component
 * function MyComponent() {
 *   const { hasPermission, roles } = usePermissions();
 *
 *   if (hasPermission('hazards', 'create')) {
 *     return <CreateHazardButton />;
 *   }
 * }
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token, user } = useAuth();

  // const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API_BASE_URL = `${BACKEND_URL}/api`;

  /**
   * Load user permissions from API
   */
  const loadPermissions = useCallback(async () => {
    if (!token) {
      setPermissions([]);
      setRoles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/rbac/my-permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load permissions');
      }

      const data = await response.json();
      setPermissions(data.permissions || []);
      setRoles(data.roles || []);
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setError(err.message);
      setPermissions([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [token, API_BASE_URL]);

  // Load permissions on mount and when token changes
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((resource, action) => {
    return permissions.some(
      p => p.resource === resource && p.action === action
    );
  }, [permissions]);

  /**
   * Check if user has ANY of the specified permissions (OR logic)
   */
  const hasAnyPermission = useCallback((permissionList) => {
    return permissionList.some(([resource, action]) =>
      permissions.some(p => p.resource === resource && p.action === action)
    );
  }, [permissions]);

  /**
   * Check if user has ALL of the specified permissions (AND logic)
   */
  const hasAllPermissions = useCallback((permissionList) => {
    return permissionList.every(([resource, action]) =>
      permissions.some(p => p.resource === resource && p.action === action)
    );
  }, [permissions]);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback((roleName) => {
    return roles.some(r => r.name === roleName || r.name.toLowerCase() === roleName.toLowerCase());
  }, [roles]);

  /**
   * Check if user is admin (convenience method)
   */
  const isAdmin = useCallback(() => {
    return hasRole('Admin') || hasRole('admin');
  }, [hasRole]);

  /**
   * Get all permissions for a specific resource
   */
  const getResourcePermissions = useCallback((resource) => {
    return permissions.filter(p => p.resource === resource);
  }, [permissions]);

  /**
   * Get permission strings (e.g., ["hazards:create", "users:read"])
   */
  const getPermissionStrings = useCallback(() => {
    return permissions.map(p => `${p.resource}:${p.action}`);
  }, [permissions]);

  /**
   * Refresh permissions (e.g., after role assignment)
   */
  const refreshPermissions = useCallback(async () => {
    await loadPermissions();
  }, [loadPermissions]);

  const value = {
    permissions,
    roles,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isAdmin,
    getResourcePermissions,
    getPermissionStrings,
    refreshPermissions,
    user
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Hook to access permission context
 *
 * @returns {object} Permission context
 * @throws {Error} If used outside PermissionProvider
 */
export function usePermissions() {
  const context = useContext(PermissionContext);

  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }

  return context;
}

/**
 * HOC to require permission for a component
 *
 * @param {ReactComponent} Component - Component to wrap
 * @param {string} resource - Required resource
 * @param {string} action - Required action
 * @param {ReactComponent} Fallback - Component to show if permission denied
 *
 * @example
 * const ProtectedComponent = withPermission(
 *   MyComponent,
 *   'hazards',
 *   'delete',
 *   () => <div>Access Denied</div>
 * );
 */
export function withPermission(Component, resource, action, Fallback = null) {
  return function PermissionWrappedComponent(props) {
    const { hasPermission, loading } = usePermissions();

    if (loading) {
      return <div>Loading...</div>;
    }

    if (!hasPermission(resource, action)) {
      return Fallback ? <Fallback /> : null;
    }

    return <Component {...props} />;
  };
}

/**
 * Component to conditionally render based on permission
 *
 * @example
 * <PermissionGate resource="hazards" action="delete">
 *   <button>Delete Hazard</button>
 * </PermissionGate>
 *
 * // With fallback
 * <PermissionGate
 *   resource="hazards"
 *   action="approve"
 *   fallback={<p>You cannot approve hazards</p>}
 * >
 *   <button>Approve</button>
 * </PermissionGate>
 *
 * // With OR logic (any permission)
 * <PermissionGate
 *   permissions={[
 *     ['reports', 'read'],
 *     ['analytics', 'read']
 *   ]}
 *   logic="OR"
 * >
 *   <ReportsLink />
 * </PermissionGate>
 */
export function PermissionGate({ resource, action, permissions, logic = 'AND', children, fallback = null }) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();

  if (loading) {
    return null;
  }

  let hasAccess = false;

  if (permissions) {
    // Multiple permissions check
    hasAccess = logic === 'OR'
      ? hasAnyPermission(permissions)
      : hasAllPermissions(permissions);
  } else if (resource && action) {
    // Single permission check
    hasAccess = hasPermission(resource, action);
  }

  return hasAccess ? children : fallback;
}

/**
 * Component to show content based on role
 *
 * @example
 * <RoleGate role="Admin">
 *   <AdminPanel />
 * </RoleGate>
 *
 * <RoleGate roles={['Admin', 'HSE Manager']}>
 *   <SafetyDashboard />
 * </RoleGate>
 */
export function RoleGate({ role, roles, children, fallback = null }) {
  const { hasRole, loading } = usePermissions();

  if (loading) {
    return null;
  }

  let hasAccess = false;

  if (roles) {
    // Multiple roles (OR logic)
    hasAccess = roles.some(r => hasRole(r));
  } else if (role) {
    // Single role
    hasAccess = hasRole(role);
  }

  return hasAccess ? children : fallback;
}
